import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import Header from './components/Header';
import FeaturedAthletes from './components/FeaturedAthletes';
import GetFeatured from './components/GetFeatured';
import AthleteDetails from './components/AthleteDetails';
import NominationScreen from './dashboard/pages/NominationScreen';
import FeaturedAthletesScreen from './dashboard/pages/FeaturedAthletesScreen';
import AnalyticsScreen from './dashboard/pages/AnalyticsScreen';
import EmailListScreen from './dashboard/pages/EmailListScreen';
import WelcomeScreen from './user/pages/WelcomeScreen';
import AboutUs from './user/pages/AboutUs';
import PrivacyPolicy from './user/pages/PrivacyPolicy';
import TermsOfService from './user/pages/TermsOfService';
import UserFooter from './user/components/UserFooter';
import AdminLoginScreen from './dashboard/pages/loginscreen';
import LandingPageScreen from './dashboard/pages/LandingPageScreen';
import StandoutsLanding from './standouts/pages/StandoutsLanding';
import WeeklyStandouts from './standouts/pages/WeeklyStandouts';
import StandoutDetail from './standouts/pages/StandoutDetail';
import SubmitForm from './standouts/pages/SubmitForm';
import LeagueGetStarted from './standouts/pages/LeagueGetStarted';
import StandoutsScreen from './dashboard/pages/StandoutsScreen';
import OrganizationsScreen from './dashboard/pages/OrganizationsScreen';
import UsersManagement from './dashboard/pages/UsersManagement';
import BannedWordsScreen from './dashboard/pages/BannedWordsScreen';
import OrgStandouts from './standouts/pages/OrgStandouts';
import LoginScreen from './user/pages/LoginScreen';
import SignupScreen from './user/pages/SignupScreen';
import VerifyEmail from './user/pages/VerifyEmail';
import ForgotPassword from './user/pages/ForgotPassword';
import PendingApproval from './standouts/pages/PendingApproval';
import AccountApproved from './standouts/pages/AccountApproved';

// Check if user is already logged in
const isAdminLoggedIn = () => {
  const token = localStorage.getItem('adminToken');
  const expiry = localStorage.getItem('tokenExpiry');
  
  if (!token || !expiry) return false;
  
  // Check if token is expired
  const now = new Date().getTime();
  const expiryTime = parseInt(expiry, 10);
  
  if (now > expiryTime) {
    // Token expired, clear it
    localStorage.removeItem('adminToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('adminUser');
    return false;
  }
  
  return true;
};

// Protected route wrapper for dashboard pages
const ProtectedRoute = ({ children }) => {
  const isLoggedIn = isAdminLoggedIn();
  
  if (!isLoggedIn) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Admin route wrapper that checks login and redirects if already logged in
const AdminRoute = ({ children }) => {
  const isLoggedIn = isAdminLoggedIn();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (isLoggedIn) {
      // Redirect to dashboard nominations if already logged in
      navigate('/dashboard/nominations', { replace: true });
    }
  }, [isLoggedIn, navigate]);
  
  return children;
};

// Public route wrapper that redirects to dashboard if admin is logged in
const PublicRoute = ({ children }) => {
  const isLoggedIn = isAdminLoggedIn();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (isLoggedIn) {
      // Redirect to dashboard if admin is logged in
      navigate('/dashboard/nominations', { replace: true });
    }
  }, [isLoggedIn, navigate]);
  
  return isLoggedIn ? null : children;
};

// Scroll to top on every route change (fixes mobile mid-page open bug)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Wrapper component for spotlight route
function SpotlightPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const setActiveScreen = (screen) => {
    if (screen === 'featured-athletes') {
      navigate('/');
    } else if (screen === 'get-featured') {
      navigate('/get-featured');
    }
  };

  return <AthleteDetails setActiveScreen={setActiveScreen} athleteId={id} />;
}

// Main app content with state-based navigation for non-spotlight routes
function AppContent() {
  const [activeScreen, setActiveScreen] = useState('featured-athletes');
  const [selectedAthleteId, setSelectedAthleteId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is on a dashboard route and handle auth
  useEffect(() => {
    const path = window.location.pathname;

    if (path.startsWith('/dashboard/')) {
      if (!isAdminLoggedIn()) {
        navigate('/admin', { replace: true });
      }
    }
  }, [navigate]);


  // Handle navigation that needs URL changes
  const handleSetActiveScreen = (screen, athleteId = null) => {
    if (screen === 'athlete-details' && athleteId) {
      navigate(`/spotlight/${athleteId}`);
    } else if (screen === 'dashboard-nominations') {
      navigate('/dashboard/nominations');
    } else if (screen === 'dashboard-featured') {
      navigate('/dashboard/featured');
    } else if (screen === 'dashboard-analytics') {
      navigate('/dashboard/analytics');
    } else if (screen === 'dashboard-email-list') {
      navigate('/dashboard/email-list');
    } else {
      setActiveScreen(screen);
    }
  };

  // Wrapper for FeaturedAthletes that navigates to spotlight URL
  const handleSelectAthlete = (athleteId, orderPosition) => {
    navigate(`/spotlight/${athleteId}`, { state: { orderPosition } });
  };

  // If admin is logged in, don't show the public site
  if (isAdminLoggedIn()) {
    return null; // Will be redirected by PublicRoute
  }

  return (
    <div className="App">
      {activeScreen === 'aboutus' ? (
        <AboutUs setActiveScreen={setActiveScreen} />
      ) : activeScreen === 'welcome' ? (
        <WelcomeScreen setActiveScreen={setActiveScreen} />
      ) : (
        <>
          <Header activeScreen={activeScreen} setActiveScreen={setActiveScreen} />
          <FeaturedAthletes
            setActiveScreen={setActiveScreen}
            setSelectedAthleteId={handleSelectAthlete}
          />
          <UserFooter />
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public routes - only accessible if NOT logged in as admin */}
          <Route path="/spotlight/:id" element={<SpotlightPage />} />

          <Route path="/:id" element={<SpotlightPage />} />

          <Route path="/" element={
            <PublicRoute>
              <AppContent />
            </PublicRoute>
          } />

          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* Admin login route - redirects to dashboard if already logged in */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLoginScreen />
            </AdminRoute>
          } />
          
          {/* Protected dashboard routes */}
          <Route path="/dashboard/nominations" element={
            <ProtectedRoute>
              <NominationScreen />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard/featured" element={
            <ProtectedRoute>
              <FeaturedAthletesScreen />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard/analytics" element={
            <ProtectedRoute>
              <AnalyticsScreen />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard/email-list" element={
            <ProtectedRoute>
              <EmailListScreen />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/landing-page" element={
            <ProtectedRoute>
              <LandingPageScreen />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/standouts" element={
            <ProtectedRoute>
              <StandoutsScreen />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/organizations" element={
            <ProtectedRoute>
              <OrganizationsScreen />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/users" element={
            <ProtectedRoute>
              <UsersManagement />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/banned-words" element={
            <ProtectedRoute>
              <BannedWordsScreen />
            </ProtectedRoute>
          } />

          {/* Get Featured route */}
          <Route path="/get-featured" element={
            <PublicRoute>
              <>
                <Header activeScreen="get-featured" setActiveScreen={(screen) => {
                  if (screen === 'featured-athletes') {
                    window.location.href = '/';
                  }
                }} />
                <GetFeatured />
                <UserFooter />
              </>
            </PublicRoute>
          } />

          {/* Public Standouts routes */}
          <Route path="/standouts" element={<StandoutsLanding />} />
          <Route path="/standouts/org/:orgSlug" element={<OrgStandouts />} />
          <Route path="/standouts/week/:weekId" element={<WeeklyStandouts />} />
          <Route path="/standouts/athlete/:id" element={<StandoutDetail />} />
          <Route path="/standouts/submit" element={<SubmitForm />} />
          <Route path="/standouts/leagues" element={<LeagueGetStarted />} />

          {/* Standouts Auth routes */}
          <Route path="/standouts/login" element={<LoginScreen />} />
          <Route path="/standouts/signup" element={<SignupScreen />} />
          <Route path="/standouts/verify-email" element={<VerifyEmail />} />
          <Route path="/standouts/forgot-password" element={<ForgotPassword />} />
          <Route path="/standouts/pending-approval" element={<PendingApproval />} />
          <Route path="/standouts/account-approved" element={<AccountApproved />} />

          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;